import { useState, useRef, useEffect } from 'react';
import './ChatBot.css';
import logoImg from '../assets/logo.png';
import Groq from 'groq-sdk';
import { SYSTEM_PROMPT, LINK_CHIPS, parseAIResponse } from '../data/chatbot';

const WELCOME_TEXT  = "Hey! 👋 I'm **Jinx**, MMC's AI assistant.\nAsk me anything — about our services, ecommerce advice, or what MMC can do for your brand.";
const WELCOME_CHIPS = ['Shopify Development', 'Marketplace Management', 'SEO Services', 'Pricing'];

function getClient() {
  const key = process.env.REACT_APP_GROQ_API_KEY;
  if (!key || key === 'your_groq_api_key_here') throw new Error('missing-key');
  return new Groq({ apiKey: key, dangerouslyAllowBrowser: true });
}

async function callGroq(history, retry = 0) {
  const client = getClient();
  try {
    const completion = await client.chat.completions.create({
      model: 'llama-3.1-8b-instant',
      messages: [{ role: 'system', content: SYSTEM_PROMPT }, ...history],
      max_tokens: 220,
      temperature: 0.72,
    });
    return completion.choices[0].message.content;
  } catch (err) {
    // 429 rate limit — wait and retry once
    if (err.status === 429 && retry === 0) {
      await new Promise(r => setTimeout(r, 12000));
      return callGroq(history, 1);
    }
    throw err;
  }
}

function formatText(text) {
  return text.split('\n').map((line, i) => {
    const html = line
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>');
    return (
      <span key={i} dangerouslySetInnerHTML={{ __html: html || '&nbsp;' }} />
    );
  }).reduce((acc, el, i) =>
    i === 0 ? [el] : [...acc, <br key={`br${i}`} />, el], []
  );
}

export default function ChatBot() {
  const [open,    setOpen]    = useState(false);
  const [msgs,    setMsgs]    = useState([
    { from: 'bot', text: WELCOME_TEXT, chips: WELCOME_CHIPS },
  ]);
  const [history, setHistory] = useState([]); // [{role, content}] for Groq
  const [input,   setInput]   = useState('');
  const [typing,  setTyping]  = useState(false);
  const [started, setStarted] = useState(false);
  const [error,   setError]   = useState(false);
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgs, typing]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const addMsg = (from, text, chips = []) => {
    setMsgs(m => [...m, { from, text, chips }]);
  };

  const send = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || typing) return;

    setInput('');
    setError(false);
    addMsg('user', trimmed);

    const nextHistory = [...history, { role: 'user', content: trimmed }];
    setTyping(true);
    // switch to retry label after 3s if still waiting (indicates rate-limit wait)
    const retryTimer = setTimeout(() => setTyping('retry'), 3000);

    try {
      const raw = await callGroq(nextHistory);
      const { text: replyText, chips } = parseAIResponse(raw);

      clearTimeout(retryTimer);
      setHistory([...nextHistory, { role: 'assistant', content: raw }]);
      setTyping(false);
      addMsg('bot', replyText, chips);
    } catch (err) {
      clearTimeout(retryTimer);
      console.error('ChatBot error:', err.message);
      setTyping(false);
      setError(true);
      if (err.message === 'missing-key') {
        addMsg('bot',
          "The AI needs a restart to pick up the API key. Stop the dev server, run **npm start** again, then try.",
          []
        );
      } else {
        addMsg('bot',
          "Sorry, I hit a connection issue. Check the browser console (F12) for details — or reach us directly.",
          ['WhatsApp Us', 'Send Email']
        );
      }
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); }
  };

  const handleChip = (chip) => {
    const action = LINK_CHIPS[chip];
    if (action) {
      if (action.close) setOpen(false);
      if (action.external) window.open(action.href, '_blank', 'noopener noreferrer');
      else window.location.href = action.href;
      return;
    }
    send(chip);
  };

  const [menuOpen, setMenuOpen] = useState(false);

  const handleMainBtn = () => {
    if (open) {
      setOpen(false);
      setMenuOpen(false);
    } else {
      setMenuOpen(o => !o);
    }
  };

  const handleBotClick = () => {
    setOpen(true);
    setMenuOpen(false);
    setStarted(true);
  };

  const handleWaClick = () => {
    window.open('https://wa.me/919527420924', '_blank', 'noopener noreferrer');
    setMenuOpen(false);
  };

  return (
    <div className="cb-wrap">

      {/* Chat window */}
      <div className={`cb-window${open ? ' cb-window--open' : ''}`} role="dialog" aria-label="MMC Chat">

        {/* Header */}
        <div className="cb-head">
          <div className="cb-head-left">
            <div className="cb-avatar">
              <img src={logoImg} alt="MMC" className="cb-avatar-img" />
              <span className="cb-online" />
            </div>
            <div className="cb-head-info">
              <span className="cb-head-name">Jinx — MMC Assistant</span>
              <span className="cb-head-status">
                {typing === 'retry' ? 'Rate limited, retrying…' : typing ? 'Typing…' : 'Online · AI-powered'}
              </span>
            </div>
          </div>
          <button className="cb-close" onClick={() => setOpen(false)} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Messages */}
        <div className="cb-body">
          {msgs.map((msg, i) => (
            <div key={i} className={`cb-msg-wrap cb-msg-wrap--${msg.from}`}>
              {msg.from === 'bot' && (
                <div className="cb-bot-avatar">
                  <img src={logoImg} alt="" />
                </div>
              )}
              <div className="cb-msg-col">
                <div className={`cb-bubble cb-bubble--${msg.from}`}>
                  {formatText(msg.text)}
                </div>
                {msg.chips?.length > 0 && (
                  <div className="cb-chips">
                    {msg.chips.map(chip => {
                      const isLink = !!LINK_CHIPS[chip];
                      return (
                        <button
                          key={chip}
                          className={`cb-chip${isLink ? ' cb-chip--action' : ''}`}
                          onClick={() => handleChip(chip)}
                        >
                          {isLink && (
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                              <polyline points="15 3 21 3 21 9"/>
                              <line x1="10" y1="14" x2="21" y2="3"/>
                            </svg>
                          )}
                          {chip}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Typing dots */}
          {typing && (
            <div className="cb-msg-wrap cb-msg-wrap--bot">
              <div className="cb-bot-avatar">
                <img src={logoImg} alt="" />
              </div>
              <div className="cb-typing">
                <span /><span /><span />
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="cb-foot">
          <input
            ref={inputRef}
            className="cb-input"
            type="text"
            placeholder="Ask me anything…"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            disabled={typing}
          />
          <button
            className="cb-send"
            onClick={() => send(input)}
            disabled={!input.trim() || typing}
            aria-label="Send"
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"/>
              <polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Speed dial */}
      <div className="cb-dial">

        {/* Options — slide up when menuOpen */}
        <div className={`cb-dial-menu${menuOpen ? ' cb-dial-menu--open' : ''}`}>

          <div className="cb-dial-item" onClick={handleWaClick}>
            <span className="cb-dial-label">WhatsApp</span>
            <button className="cb-dial-btn cb-dial-btn--wa" aria-label="WhatsApp">
              <svg width="23" height="23" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
              </svg>
            </button>
          </div>

          <div className="cb-dial-item" onClick={handleBotClick}>
            <span className="cb-dial-label">Ask Jinx</span>
            <button className="cb-dial-btn cb-dial-btn--bot" aria-label="Chat with Jinx">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
            </button>
          </div>

        </div>

        {/* Main FAB */}
        <button
          className={`cb-btn${(menuOpen || open) ? ' cb-btn--open' : ''}`}
          onClick={handleMainBtn}
          aria-label="Contact us"
        >
          {!started && !menuOpen && <span className="cb-unread">1</span>}
          <svg className="cb-btn__chat" width="24" height="24" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          <svg className="cb-btn__close" width="20" height="20" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

      </div>
    </div>
  );
}
